import { useState } from "react";
import { Check, Copy, Eye, EyeOff } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

/**
 * MaskedField — displays a secret token as •••••••• with reveal + copy.
 * The value is *only* known client-side (e.g. after a Test Connection
 * response) — we never source it from the frontend bundle.
 *
 * Prop `present` toggles between "configured / masked" vs "not set" states.
 */
export default function MaskedField({
  label,
  value = "",
  present = false,
  canReveal = false,
  onTest,
  testId,
  helper,
}) {
  const { t } = useT();
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);
  const [testState, setTestState] = useState(""); // "", "ok", "fail"

  const masked = "••••••••••••••••";
  const display = present ? (shown && value ? value : masked) : t("admin.label.notSet");

  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };

  const runTest = async () => {
    if (!onTest) return;
    setTestState("");
    try {
      const ok = await onTest();
      setTestState(ok ? "ok" : "fail");
    } catch {
      setTestState("fail");
    }
    setTimeout(() => setTestState(""), 2400);
  };

  return (
    <div className="border border-white/10 bg-white/[0.02] p-4 md:p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-[0.28em] uppercase text-bone/60">{label}</p>
        <span
          className={`text-[10px] tracking-[0.22em] uppercase px-2 py-0.5 border ${present ? "text-emerald-400 border-emerald-500/40" : "text-red-400 border-red-500/40"}`}
        >
          {present ? t("admin.label.configured") : t("admin.label.notSet")}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 font-mono text-[13px] text-bone/85 truncate select-all">
          {display}
        </code>
        {canReveal && value && (
          <button
            type="button"
            onClick={() => setShown((v) => !v)}
            className="p-2 text-bone/60 hover:text-gold transition-colors"
            aria-label={shown ? t("admin.action.hide") : t("admin.action.reveal")}
          >
            {shown ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}
          </button>
        )}
        {value && (
          <button
            type="button"
            onClick={copy}
            className="p-2 text-bone/60 hover:text-gold transition-colors"
            aria-label={t("admin.action.copy")}
          >
            {copied ? <Check size={14} strokeWidth={1.75} className="text-emerald-400" /> : <Copy size={14} strokeWidth={1.5} />}
          </button>
        )}
      </div>
      {(onTest || helper) && (
        <div className="mt-3 flex items-center gap-3">
          {onTest && (
            <button
              type="button"
              onClick={runTest}
              className="text-[10px] tracking-[0.28em] uppercase text-gold hover:text-bone border border-gold/40 px-3 py-1.5"
            >
              {t("admin.action.testConnection")}
            </button>
          )}
          {testState === "ok" && <span className="text-[11px] text-emerald-400">✓ {t("admin.label.connected")}</span>}
          {testState === "fail" && <span className="text-[11px] text-red-400">✗ {t("admin.label.disconnected")}</span>}
          {helper && !testState && <span className="text-[11px] text-bone/50">{helper}</span>}
        </div>
      )}
    </div>
  );
}
